import { observable, action, decorate } from 'mobx';
import { ALREADY_SUBMITTED, PAGE_LOADER } from '../../logic/Constants';
import {
  INVALID_ORDER,
  ORDER_OUTSIDE_TIMEFRAME,
} from '../../logic/GetOrderError';
import isValidReturn from './isValidReturn';
import { initialiseSteps, Step } from '../../logic/Steps';
import getReturnPayload from './getReturnPayload';
import getQuotePayload from './getQuotePayload';
import isManualApprovalRequired from './isManualApprovalRequired';
import { formatDate, formatDateShort } from '../../logic/FormatDate';
import { addCustomDatadogError } from '../../utils/datadog';
import Fetch from '../../fetch/Fetch';
import AddressStore from '../AddressStore';
import { Item, Order, ReturnDetails, ReturnItem } from '../../types/order';
import ErrorStore from '../ErrorStore';
import InteractStore from '../InteractStore';
import {
  ErrorResponse,
  LabelResponse,
  OrderResponse,
  QuotesResponse,
  SubmitReturnResponse,
} from '../../fetch/types';
import ReturnMethodStore from '../ReturnMethodStore';
import {
  formatCurrency,
  getCurrencyCode,
} from '../../components/util/Currency';
import shouldRejectOutsideWindow from './shouldRejectOutsideWindow';

class OrderStore {
  private transport: Fetch;

  private address: AddressStore;

  private returnMethod: ReturnMethodStore;

  public errors: ErrorStore;

  public order: Order | null;

  private steps: Array<Step> | null;

  public items: Array<ReturnItem> | null;

  private merchantReference: string | null;

  private trackingNumber: string | null;

  private labelUrl: string | null;

  private pickUpRequestUrl: string | null;

  private courierJobId: string | null;

  private refundMethodId: number | null;

  constructor(
    transport: Fetch,
    addressStore: AddressStore,
    returnMethod: ReturnMethodStore,
    errors: ErrorStore
  ) {
    this.transport = transport;
    this.address = addressStore;
    this.returnMethod = returnMethod;
    this.errors = errors;
    this.order = null;
    this.steps = null;
    this.items = null;
    this.merchantReference = null;
    this.trackingNumber = null;
    this.labelUrl = null;
    this.pickUpRequestUrl = null;
    this.courierJobId = null;
    this.refundMethodId = null;
  }

  setMerchantReference(merchantReference: string) {
    this.merchantReference = merchantReference;
  }

  getOrder() {
    return this.order!;
  }

  setOrder(order: Order) {
    this.order = order;
    const currencyCode = getCurrencyCode(
      order.shippit_connect_purchase_data.return_request,
      order.merchant.currency_code
    );

    this.items = order.shippit_connect_purchase_data.return_request.items.map(
      item => ({
        ...item,
        formatted_price: formatCurrency(Number(item.price), currencyCode),
      })
    ) as ReturnItem[];

    this.address.set(
      order.shipping_address_validation.is_valid,
      order.shippit_connect_purchase_data.return_request.shipping_address,
      order.shippit_connect_purchase_data.return_request.billing_address
    );
    this.returnMethod.set(
      order.merchant.returns_configuration.settings,
      order.merchant.country_code
    );
    this.returnMethod.setReturnsRequest(
      order.shippit_connect_purchase_data.return_request,
      order.merchant.currency_code
    );
    this.steps = initialiseSteps(
      order.merchant.returns_configuration.settings,
      this.merchantReference!
    );
  }

  setItemAsReturn(item: ReturnItem) {
    // eslint-disable-next-line no-param-reassign
    this.items?.forEach(p => delete p.isActiveReturn);
    // eslint-disable-next-line no-param-reassign
    item.isActiveReturn = true;
  }

  setReturnDetails(data: ReturnDetails) {
    if (data.quantity === '') {
      // eslint-disable-next-line no-param-reassign
      data.quantity = '1';
    }

    const item = this.items!.find(p => p.isActiveReturn);
    // eslint-disable-next-line no-param-reassign
    item!.returnDetails = data;
  }

  setLabel({
    labelUrl,
    pickUpRequestUrl,
    courierJobId,
  }: {
    labelUrl: string;
    pickUpRequestUrl: string;
    courierJobId: string;
  }) {
    this.labelUrl = labelUrl;
    this.pickUpRequestUrl = pickUpRequestUrl;
    this.courierJobId = courierJobId;
  }

  removeReturnDetails(item: Item) {
    const product = this.items!.find(p => p.id === item.id);
    delete product?.returnDetails;
    delete product?.isActiveReturn;
  }

  getSteps() {
    return this.steps!;
  }

  getActiveReturn() {
    return this.items!.find(p => p.isActiveReturn)!;
  }

  getProductsToBeReturned() {
    return this.items!.filter(product => product.returnDetails);
  }

  getItemById(id: number) {
    return this.items!.find(i => i.id === id);
  }

  getOrderReference() {
    return this.order!.purchase_reference;
  }

  getPrettyOrderReference() {
    return this.getOrderReference().toUpperCase();
  }

  getReturnConditions() {
    return this.order!.merchant.returns_configuration.conditions;
  }

  getReturnReasons() {
    return this.order!.merchant.returns_configuration.reasons;
  }

  getReturnRules() {
    return this.order!.merchant.returns_configuration.rules;
  }

  getPurchasedAt() {
    return this.order!.purchased_at!;
  }

  getReturnWindow() {
    return this.order?.merchant.returns_configuration.settings
      .return_window_days!;
  }

  getRejectExpiredReturns() {
    return this.order?.merchant.returns_configuration.settings
      .reject_expired_returns!;
  }

  getExpiresAt() {
    return this.order!.expires_at;
  }

  getMerchantReference() {
    return this.merchantReference!;
  }

  getMerchants() {
    return this.order!.shippit_connect_purchase_data.return_request.merchants!;
  }

  getMerchantName() {
    return this.order!.merchant.returns_configuration.settings
      .returns_store_name!;
  }

  getStoreLocatorUrl() {
    return this.order?.merchant?.returns_configuration?.settings
      ?.store_locator_url;
  }

  getSelfReturnsUrl() {
    return this.order?.merchant?.returns_configuration?.settings
      ?.self_returns_url;
  }

  getMerchantAddress() {
    return (
      this.order?.merchant?.returns_configuration?.settings?.merchant_address ||
      ''
    );
  }

  formatPurchaseDate() {
    const purchasedAt = this.getPurchasedAt();
    if (purchasedAt) {
      return formatDateShort(new Date(purchasedAt));
    }
    return undefined;
  }

  formatReturnWindow() {
    const days = this.getReturnWindow();

    const purchasedAt = this.getPurchasedAt();
    if (!purchasedAt) return undefined;

    const purchasedAtDate = new Date(purchasedAt);
    purchasedAtDate.setDate(purchasedAtDate.getDate() + days!);
    return formatDate(purchasedAtDate);
  }

  formatExpiresAt() {
    const expiresAt = this.getExpiresAt();
    if (expiresAt) {
      return formatDate(new Date(expiresAt));
    }
    return undefined;
  }

  getReturnPayload() {
    return getReturnPayload({
      returnRequestId: this.order!.id,
      merchantId: this.order!.merchant.id,
      orderReference: this.getOrderReference()!,
      merchants: this.getMerchants()!,
      products: this.getProductsToBeReturned(),
      customerEmail: this.order!.customer_email!,
      shippingDetails: this.address.getShippingAddress()!,
      returnMethod: this.returnMethod,
      refundMethodId: this.getRefundMethodId(),
    });
  }

  setTrackingNumber(trackingNumber: string) {
    this.trackingNumber = trackingNumber;
  }

  getTrackingNumber() {
    return this.trackingNumber;
  }

  getLabelUrl() {
    return this.labelUrl;
  }

  getPickUpRequestUrl() {
    return this.pickUpRequestUrl;
  }

  getCourierJobId() {
    return this.courierJobId;
  }

  getReturnRefundMethods() {
    return this.order?.merchant.returns_configuration.refund_methods || [];
  }

  getRefundMethodId() {
    return this.refundMethodId;
  }

  getRefundMethodName() {
    return this.getReturnRefundMethods()?.find(
      r => r.id === this.getRefundMethodId()
    )?.description;
  }

  setRefundMethodId(refundMethodId: number) {
    this.refundMethodId = refundMethodId;
  }

  exists() {
    return !!this.order;
  }

  isProductAddedToReturns() {
    return this.items?.some(item => item.returnDetails);
  }

  isManualApprovalRequired() {
    return isManualApprovalRequired(
      this.getProductsToBeReturned(),
      this.getReturnRules()!,
      this.getPurchasedAt()!,
      this.getReturnWindow()!,
      this.getExpiresAt()!
    );
  }

  isReturnWindowDisabled() {
    return !this.getReturnWindow();
  }

  async fetchOrder(id: string, email: string, orderReference: string) {
    try {
      const response = await this.transport.fetchOrder(
        this.merchantReference!,
        email,
        orderReference
      );
      if ((response as ErrorResponse)?.errors) {
        this.errors.set(
          id,
          (response as ErrorResponse).errors[0],
          this.errors.WARN
        );

        addCustomDatadogError(
          `Return request: error response from the api for merchant reference: ${this.merchantReference}, email: ${email} and OrderReference: ${orderReference}`,
          (response as ErrorResponse).errors
        );

        return false;
      }

      if (shouldRejectOutsideWindow(response as OrderResponse)) {
        this.errors.set(
          id,
          { message: ORDER_OUTSIDE_TIMEFRAME },
          this.errors.WARN
        );
        return false;
      }

      if (isValidReturn(response as OrderResponse)) {
        this.setOrder(response as Order);
        this.transport.setToken((response as OrderResponse).token);
        return true;
      }
      this.errors.set(id, { message: INVALID_ORDER }, this.errors.WARN);
    } catch (error: any) {
      this.errors.set(id, error, this.errors.WARN);

      addCustomDatadogError(
        `Return request: while creating a return request for merchant reference: ${this.merchantReference}, email: ${email} and OrderReference: ${orderReference}`,
        error
      );
    }
    return false;
  }

  async submitReturn(id: string) {
    let status = true;
    try {
      const response = (await this.transport.submitReturn(
        this.getReturnPayload()
      )) as SubmitReturnResponse;

      this.setTrackingNumber(response.tracking_number);
    } catch (error: any) {
      status = error ? error.code === ALREADY_SUBMITTED : false;
      this.errors.set(id, error, this.errors.FATAL);

      addCustomDatadogError(
        `Submit Return: issue while creating submit return order for merchant id: ${this.order?.merchant.id}`,
        error
      );
    }
    return status;
  }

  async fetchLabel(id: string, attempts: number) {
    try {
      const response = await this.transport.getLabel(
        {
          purchase_reference: this.getOrderReference()!,
          customer_email: this.order?.customer_email!,
          request_id: this.order?.id!,
          merchant_id: this.order?.merchant.id!,
        },
        this.getTrackingNumber()!
      );

      if ((response as ErrorResponse)?.errors) {
        this.errors.set(
          id,
          (response as ErrorResponse).errors[0],
          this.errors.WARN
        );

        addCustomDatadogError(
          `Fetch label: error response from the api ${this.order?.merchant.id}`,
          (response as ErrorResponse).errors
        );
      }

      const {
        label_url: labelUrl,
        pickup_request_url: pickUpRequestUrl,
        courier_job_id: courierJobId,
      } = (response as LabelResponse).order;
      this.setLabel({ labelUrl, pickUpRequestUrl, courierJobId });
      return {
        labelUrl: this.getLabelUrl(),
        pickUpRequestUrl: this.getPickUpRequestUrl(),
      };
    } catch (error: any) {
      if (attempts === 4) {
        this.errors.set(id, {
          message: 'Sorry, we’re unable to generate your shipping label.',
        });
      } else {
        this.errors.set(id, error);
      }

      addCustomDatadogError(
        `Fetch label: issue while generating order label for merchant id: ${this.order?.merchant.slug}`,
        error
      );
    }
    return undefined;
  }

  byPassQuotesApiResponse() {
    // NOTE: assume eParcel is the only option to return. We are doing this
    // for the TAF issue with quotes.
    this.returnMethod.setRecommendation({
      quotes: {
        recommendations: [
          {
            carrier_type: 'Eparcel',
            carrier_name: 'eParcel',
            service_level: 'normal',
            dropoff: true,
          },
        ],
      },
    } as any);
  }

  async fetchMethods(interact: InteractStore, id: string) {
    interact.startSubmit(PAGE_LOADER);
    try {
      const merchants = this.getMerchants();
      if (merchants && merchants.length > 0) {
        this.byPassQuotesApiResponse();
      } else {
        const response = await this.transport.getQuotes(
          getQuotePayload(
            this.order?.merchant.slug!,
            this.address.getShippingAddress()!,
            this.getProductsToBeReturned()
          )
        );
        if ((response as ErrorResponse)?.errors) {
          this.errors.set(
            id,
            (response as ErrorResponse).errors[0],
            this.errors.WARN
          );

          addCustomDatadogError(
            `Quote: error response from the api for merchant id: ${this.order?.merchant.id}`,
            (response as any).errors
          );
        }

        this.returnMethod.setRecommendation(response as QuotesResponse);
        this.errors.set(id, { message: this.returnMethod.getError() });
      }

      interact.stopSubmit(PAGE_LOADER);
      return true;
    } catch (error: any) {
      this.errors.set(id, error);

      addCustomDatadogError(
        `Quote: issue while adding return methods for merchant id; ${this.order?.merchant.slug}`,
        error
      );
    }
    interact.stopSubmit(PAGE_LOADER);
    return false;
  }
}

decorate<any>(OrderStore, {
  pickUpRequestUrl: observable,
  labelUrl: observable,
  courierJobId: observable,
  items: observable,
  setLabel: action,
  setOrder: action,
  setItemAsReturn: action,
  setReturnDetails: action,
  removeReturnDetails: action,
});

export default OrderStore;
